import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Adatkor { adatkor: string; megnevezes: string; leiras: string; }
interface Kulcs {
  id: number; token_eleje: string; megnevezes: string; adatkorok: string[]; letrehozta: string | null;
  letrehozva: string; visszavonva: string | null; visszavonta: string | null; utolso_keres: string | null;
  keresek: number; aktiv: boolean;
}
interface Kiadott { id: number; megnevezes: string; adatkorok: string[]; linkek: { index: string; adatkorok: Record<string, string> }; }

/**
 *  Admin → Adatexport (JSON) (backend/adatexport.js).
 *
 *  A KRÉTA-exportok (tanulók tantárgyai és pedagógusai, szülők elérhetőségei, osztályba járó
 *  tanulók, mulasztások, jegyek) gépi megfelelője egy külső kimutatásnak (BI): adatkörönként egy
 *  JSON-cím, amelyben egy hosszú, véletlen kulcs a belépő. Itt adja ki az admin a kulcsot, itt
 *  látja, mikor és hányszor kérték le, és itt vonja vissza vagy cseréli le.
 *
 *  A teljes cím CSAK a kiadás (vagy újragenerálás) pillanatában látszik – utána a lista már csak
 *  a kulcs elejét mutatja. Aki a linket megkapja, a benne lévő személyes adatokhoz hozzáfér, ezért
 *  ezt a képernyő ki is mondja.
 */
@Component({
  selector: 'app-admin-adatexport',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './adatexport.component.html',
  styleUrls: ['./adatexport.component.scss'],
})
export class AdminAdatexportComponent implements OnInit {
  intezmeny = '';
  adatkorok: Adatkor[] = [];
  kulcsok: Kulcs[] = [];
  hiba = '';
  fut = false;
  betoltve = false;

  ujNyitva = false;
  ujNev = '';
  ujAdatkorok: Record<string, boolean> = {};

  kiadott: Kiadott | null = null;
  naploNyitva: number | null = null;
  naplo: any[] = [];

  constructor(private http: HttpClient, private data: DataService) {}

  //  A tárolt intézményből (mint a többi admin-képernyő): közvetlen URL-betöltéskor az app-keret
  //  még nem regisztrált, és üres intézménnyel „nem admin" hibát kapnánk.
  async ngOnInit() {
    this.intezmeny = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.betolt();
  }

  private ut(vege: string) { return `/api/adatexport/${vege}/${this.intezmeny}`; }

  async betolt() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(this.ut('kulcsok')));
      this.adatkorok = r?.adatkorok || [];
      this.kulcsok = r?.kulcsok || [];
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A kulcsok nem tölthetők be.';
    } finally {
      this.betoltve = true;
    }
  }

  get aktivak() { return this.kulcsok.filter((k) => k.aktiv); }
  get visszavontak() { return this.kulcsok.filter((k) => !k.aktiv); }
  adatkorNev(a: string) { return this.adatkorok.find((x) => x.adatkor === a)?.megnevezes || a; }
  adatkorNevek(l: string[]) { return (l || []).map((a) => this.adatkorNev(a)).join(', '); }
  get ujKijelolt() { return this.adatkorok.filter((a) => this.ujAdatkorok[a.adatkor]).map((a) => a.adatkor); }

  ujMegnyit() {
    this.ujNev = '';
    this.ujAdatkorok = Object.fromEntries(this.adatkorok.map((a) => [a.adatkor, true]));
    this.ujNyitva = true;
  }

  private async muvelet<T>(f: () => Promise<T>, hibaSzoveg: string): Promise<T | null> {
    this.fut = true; this.hiba = '';
    try { const r = await f(); await this.betolt(); return r; }
    catch (e: any) { this.hiba = e?.error?.error || hibaSzoveg; return null; }
    finally { this.fut = false; }
  }

  async letrehoz() {
    const r = await this.muvelet(() => firstValueFrom(this.http.post<Kiadott>(this.ut('kulcsok'),
      { megnevezes: this.ujNev, adatkorok: this.ujKijelolt })), 'A kulcs nem készült el.');
    if (r) { this.ujNyitva = false; this.kiadott = r; }
  }

  async ujraGeneral(k: Kulcs) {
    if (!confirm(`Új kulcsot kérsz ehhez: „${k.megnevezes}”? A mostani címek AZONNAL érvénytelenné válnak, `
      + 'tehát az új címeket be kell állítani ott, ahol a régieket használták.')) return;
    const r = await this.muvelet(() => firstValueFrom(this.http.post<Kiadott>(this.ut(`kulcs/${k.id}/ujrageneral`), {})),
      'Az új kulcs nem készült el.');
    if (r) this.kiadott = r;
  }

  async visszavon(k: Kulcs) {
    if (!confirm(`Biztosan visszavonod ezt a kulcsot: „${k.megnevezes}”? A címei azonnal megszűnnek, `
      + 'és visszaállítani nem lehet – helyette új kulcs adható ki.')) return;
    await this.muvelet(() => firstValueFrom(this.http.post(this.ut(`kulcs/${k.id}/visszavon`), {})), 'A visszavonás nem sikerült.');
    if (this.naploNyitva === k.id) this.naploNyitva = null;
  }

  async naploValt(k: Kulcs) {
    if (this.naploNyitva === k.id) { this.naploNyitva = null; return; }
    this.naploNyitva = k.id;
    this.naplo = [];
    try {
      this.naplo = (await firstValueFrom(this.http.get<any[]>(this.ut(`kulcs/${k.id}/naplo`)))) || [];
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A napló nem tölthető be.';
    }
  }

  /*  A címet TELJES alakban adjuk: egy BI-eszközbe nem lehet „/api/…"-t beírni, oda a kiszolgáló
   *  nevével együtt kell (ugyanígy, mint a naptár-feliratkozásnál). */
  teljesCim(ut: string): string {
    if (!ut) return '';
    try { return new URL(ut, window.location.origin).toString(); } catch (e) { return ut; }
  }

  kiadottLista(): { cim: string; ut: string }[] {
    if (!this.kiadott) return [];
    return [
      { cim: 'Tartalomjegyzék (az engedélyezett adatkörök címei)', ut: this.kiadott.linkek.index },
      ...this.kiadott.adatkorok.map((a) => ({ cim: this.adatkorNev(a), ut: this.kiadott!.linkek.adatkorok[a] })),
    ];
  }

  async masol(ut: string) {
    try {
      await navigator.clipboard.writeText(this.teljesCim(ut));
      this.data.sendToast('A cím a vágólapra került.', 'success');
    } catch (e) {
      this.data.sendToast('A másolás nem sikerült – jelöld ki és másold kézzel.', 'warning');
    }
  }

  async mindetMasol() {
    const szoveg = this.kiadottLista().map((l) => `${l.cim}: ${this.teljesCim(l.ut)}`).join('\n');
    try {
      await navigator.clipboard.writeText(szoveg);
      this.data.sendToast('Minden cím a vágólapra került.', 'success');
    } catch (e) {
      this.data.sendToast('A másolás nem sikerült – jelöld ki és másold kézzel.', 'warning');
    }
  }
}
